// Shared vocabulary of the voice pages (2026-10-05 redesign): what a call's
// tool calls MEAN (outcome chips + facet category) and how call times render.
// Tool names are platform voice tools (tool_registry), not tenant-specific.
import { parseUtc } from './dates';

export const TOOL_META = {
  create_unanimity_booking: { key: 'bookingCreated', fallback: 'Réservation créée', kind: 'action' },
  cancel_reservation: { key: 'reservationCancelled', fallback: 'Annulation', kind: 'action' },
  send_sms_to_caller: { key: 'smsSent', fallback: 'SMS envoyé', kind: 'action' },
  create_edn_ticket: { key: 'ticketCreated', fallback: 'Ticket créé', kind: 'action' },
  call_owner: { key: 'transferredToOwner', fallback: 'Transféré au gérant', kind: 'escalation' },
  check_restaurant_availability: { key: 'availabilityChecked', fallback: 'Disponibilité vérifiée', kind: 'lookup' },
  get_restaurant_public_menu: { key: 'menuLookedUp', fallback: 'Menu consulté', kind: 'lookup' },
  retrieve_tracking_data: { key: 'trackingLookedUp', fallback: 'Suivi colis consulté', kind: 'lookup' },
  get_my_reservations: { key: 'reservationsLookedUp', fallback: 'Réservations consultées', kind: 'lookup' },
  get_edn_ticket_topics: { key: 'topicsLookedUp', fallback: 'Motifs consultés', kind: 'lookup' },
  end_call: { kind: 'ignore' },
};

const prettify = (name) => String(name || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// Deduped chips for a call: actions first, then escalations, then (optionally)
// lookups. `t` resolves the label through i18n.
export const outcomeChips = (toolNames, t, { includeLookups = false } = {}) => {
  const seen = new Set();
  const chips = [];
  (toolNames || []).forEach((name) => {
    const meta = TOOL_META[name];
    const kind = meta?.kind || 'lookup';
    if (kind === 'ignore' || seen.has(name)) return;
    if (kind === 'lookup' && !includeLookups) return;
    seen.add(name);
    chips.push({
      name,
      kind,
      label: meta?.key
        ? t(`voiceTickets.outcomes.${meta.key}`, meta.fallback)
        : prettify(name),
    });
  });
  const order = { action: 0, escalation: 1, lookup: 2 };
  return chips.sort((a, b) => order[a.kind] - order[b.kind]);
};

// Facet category of a call: did the agent DO something, hand over, or only inform?
export const callCategory = (toolNames) => {
  const kinds = (toolNames || []).map((name) => TOOL_META[name]?.kind || 'lookup');
  if (kinds.includes('action')) return 'action';
  if (kinds.includes('escalation')) return 'escalated';
  return 'info';
};

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const total = Number(seconds);
  if (Number.isNaN(total)) return '—';
  const mins = Math.floor(total / 60);
  const secs = Math.round(total % 60);
  return `${mins} min ${String(secs).padStart(2, '0')}`;
};

// started_at arrives as naive UTC (KI-110 class): parseUtc, then display in
// the operators' timezone (Europe/Paris, the src/utils/dates.js convention).
const TZ = { timeZone: 'Europe/Paris' };

export const formatCallDay = (value) => {
  const date = parseUtc(value);
  if (!date) return '—';
  return `${date.toLocaleDateString('fr-FR', { ...TZ, day: 'numeric', month: 'short' })} ${date.toLocaleTimeString('fr-FR', { ...TZ, hour: '2-digit', minute: '2-digit' })}`;
};

export const formatCallFull = (value) => {
  const date = parseUtc(value);
  if (!date) return '—';
  return date.toLocaleString('fr-FR', { ...TZ, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const formatCallTime = (value) => {
  const date = parseUtc(value);
  if (!date) return '';
  return date.toLocaleTimeString('fr-FR', { ...TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const formatRelative = (value, t) => {
  const date = parseUtc(value);
  if (!date) return '';
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return t('voiceTickets.relMinutes', 'il y a {{n}} min', { n: Math.max(1, minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('voiceTickets.relHours', 'il y a {{n}} h', { n: hours });
  const days = Math.floor(hours / 24);
  return t('voiceTickets.relDays', 'il y a {{n}} j', { n: days });
};

export const callerInitials = (name, phone) => {
  const clean = (name || '').trim();
  if (!clean) return phone ? '?' : '–';
  return clean.split(/\s+/).map((part) => part[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
};
